/**
 * Archive loader — pulls several transcripts' NDJSON streams, one at a time, and parses them
 * into the typed arrays the ET ingests. Pure-ish: the network call is injected (`fetchStream`)
 * so the parser and the retry logic are unit-tested without a server.
 *
 * Why SEQUENTIAL: fetching several big streams concurrently truncated the later ones (the local
 * backend can't serve multiple streaming responses at once) and silently dropped most of a
 * source's clips. The normal ET also loads one at a time.
 *
 * The stream ends with a `__complete__` sentinel; without it (deploy restart, proxy timeout,
 * network blip) the load is retried before anything is shown — same rule the single ET uses.
 */

const ROW_TYPES = ["sentence", "entity", "category", "aib", "auto_highlight", "content_moderation"];

export function createStreamParser() {
  const acc = {
    metadata: null,
    complete: false,
    rowsProcessed: 0,
    sentences: [],
    entity: [],
    category: [],
    aib: [],
    auto_highlight: [],
    content_moderation: [],
  };
  const feedLine = (line) => {
    if (!line) return;
    let data;
    try { data = JSON.parse(line); } catch (e) { return; }
    if (!data) return;
    if (data.type === "__complete__") { acc.complete = true; return; }
    // Header (first line): video url, title, duration, ext, total_rows, lazy_thumbnails.
    if (!acc.metadata && data.total_rows) { acc.metadata = data; return; }
    if (!acc.metadata) return;
    if (!ROW_TYPES.includes(data.type)) return;
    acc.rowsProcessed += 1;
    if (data.type === "sentence") {
      if (data.data) acc.sentences.push(data.data);
    } else {
      acc[data.type].push(data.data);
    }
  };
  return { acc, feedLine };
}

/** Parse a whole stream body (string) — used by tests and by the non-streaming fallback. */
export function parseStreamText(text) {
  const p = createStreamParser();
  String(text || "").split("\n").forEach(p.feedLine);
  return p.acc;
}

/** A load is "incomplete" when the sentinel never arrived and fewer rows than promised did. */
export const isIncomplete = (acc) =>
  !acc.complete && (!acc.metadata || acc.rowsProcessed < (Number(acc.metadata.total_rows) || 0));

// Read a Response incrementally when it exposes a body reader (browser), else as one text blob.
async function readResponse(response, feedLine, onProgress, totalHint) {
  const body = response && response.body;
  if (!body || typeof body.getReader !== "function") {
    const text = await response.text();
    text.split("\n").forEach(feedLine);
    return;
  }
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let lines = 0;
  let lastPct = -1;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n");
    buffer = parts.pop() || "";
    for (const line of parts) {
      feedLine(line);
      lines += 1;
    }
    const total = totalHint();
    if (onProgress && total > 0) {
      const pct = Math.min(99, Math.round((lines / total) * 100));
      if (pct !== lastPct && (pct - lastPct >= 5 || pct >= 99)) { lastPct = pct; onProgress(pct); }
    }
  }
  if (buffer) feedLine(buffer);
}

/**
 * Load ONE transcript's stream.
 * @param {number|string} transcriptId
 * @param {{ fetchStream: (id) => Promise<Response>, onProgress?: (pct:number)=>void, maxAttempts?: number }} deps
 * @returns {Promise<object>} the parser accumulator + `{ transcriptId, title, incomplete, attempts }`
 */
export async function loadTranscriptStream(transcriptId, { fetchStream, onProgress, maxAttempts = 3 }) {
  if (typeof fetchStream !== "function") throw new Error("archiveLoader: fetchStream is required");
  let attempt = 0;
  let last = null;
  while (attempt < maxAttempts) {
    attempt += 1;
    const p = createStreamParser();
    const response = await fetchStream(transcriptId);
    if (!response || !response.ok) {
      const status = response ? response.status : "no response";
      if (attempt >= maxAttempts) throw new Error(`Couldn't load transcript ${transcriptId} (HTTP ${status})`);
      continue;
    }
    await readResponse(response, p.feedLine, onProgress, () => (p.acc.metadata ? Number(p.acc.metadata.total_rows) || 0 : 0));
    last = p.acc;
    if (!isIncomplete(last)) break;
  }
  if (onProgress) onProgress(100);
  return {
    ...last,
    transcriptId,
    title: (last && last.metadata && last.metadata.title) || `Transcript ${transcriptId}`,
    incomplete: !!(last && isIncomplete(last)),
    attempts: attempt,
  };
}

/**
 * Load several transcripts SEQUENTIALLY. `onProgress({ index, total, transcriptId, percent })`
 * fires per transcript so the UI can say "Loading 2 of 3 — Debate 2 (40%)".
 */
export async function loadArchive(transcriptIds, { fetchStream, onProgress, maxAttempts } = {}) {
  const ids = (transcriptIds || []).map((x) => (typeof x === "string" && /^\d+$/.test(x) ? Number(x) : x));
  const loaded = [];
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    const report = (percent) => { if (onProgress) onProgress({ index: i, total: ids.length, transcriptId: id, percent }); };
    report(0);
    const acc = await loadTranscriptStream(id, { fetchStream, maxAttempts, onProgress: report });
    loaded.push(acc);
  }
  return loaded;
}

/** `"6,9,6"` (the ?ids= query) -> `[6, 9]`. Ignores junk. */
export function parseIdsParam(value) {
  const out = [];
  const seen = new Set();
  String(value || "").split(",").forEach((s) => {
    const t = s.trim();
    if (!/^\d+$/.test(t)) return;
    const n = Number(t);
    if (seen.has(n)) return;
    seen.add(n);
    out.push(n);
  });
  return out;
}
